import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function fromISO(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatShort(date) {
  return date
    ? new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'short' }).format(date).replace('.', '')
    : 'Agregar fecha';
}

function sameDay(left, right) {
  return Boolean(left && right && toISO(left) === toISO(right));
}

export default function DateRangePicker({ checkIn, checkOut, min, onChange }) {
  const wrapperRef = useRef(null);
  const start = useMemo(() => fromISO(checkIn), [checkIn]);
  const end = useMemo(() => fromISO(checkOut), [checkOut]);
  const minimum = useMemo(() => fromISO(min), [min]);
  const [open, setOpen] = useState(false);
  const [selectingEnd, setSelectingEnd] = useState(Boolean(start && !end));
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const base = start || minimum || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  useEffect(() => {
    const close = (event) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.type === 'pointerdown' && !wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, []);

  const days = useMemo(() => {
    const first = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - offset);
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      return date;
    });
  }, [visibleMonth]);

  const chooseDate = (date) => {
    if (minimum && date < minimum) return;
    if (!start || !selectingEnd || date <= start) {
      onChange({ checkIn: toISO(date), checkOut: '' });
      setSelectingEnd(true);
      return;
    }
    onChange({ checkIn: toISO(start), checkOut: toISO(date) });
    setSelectingEnd(false);
    setOpen(false);
  };

  const toggle = () => {
    if (!open) {
      const base = start || minimum || new Date();
      setVisibleMonth(new Date(base.getFullYear(), base.getMonth(), 1));
      setSelectingEnd(Boolean(start && !end));
    }
    setOpen(value => !value);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger type="button" onClick={toggle} aria-expanded={open} aria-haspopup="dialog">
        <FiCalendar />
        <DatePart>
          <small>Llegada</small>
          <strong className={start ? '' : 'empty'}>{formatShort(start)}</strong>
        </DatePart>
        <Divider />
        <DatePart>
          <small>Salida</small>
          <strong className={end ? '' : 'empty'}>{formatShort(end)}</strong>
        </DatePart>
      </Trigger>

      {open && (
        <Calendar role="dialog" aria-label="Elegir fechas de llegada y salida">
          <CalendarTop>
            <div><small>{selectingEnd && start ? 'AHORA ELEGÍ LA SALIDA' : 'ELEGÍ TUS FECHAS'}</small><strong>{MONTHS[visibleMonth.getMonth()]} {visibleMonth.getFullYear()}</strong></div>
            <Nav>
              <button type="button" onClick={() => setVisibleMonth(value => new Date(value.getFullYear(), value.getMonth() - 1, 1))} aria-label="Mes anterior"><FiChevronLeft /></button>
              <button type="button" onClick={() => setVisibleMonth(value => new Date(value.getFullYear(), value.getMonth() + 1, 1))} aria-label="Mes siguiente"><FiChevronRight /></button>
            </Nav>
          </CalendarTop>
          <Week>{WEEKDAYS.map(day => <span key={day}>{day}</span>)}</Week>
          <Grid>
            {days.map(date => {
              const disabled = Boolean(minimum && date < minimum);
              const outside = date.getMonth() !== visibleMonth.getMonth();
              const isStart = sameDay(date, start);
              const isEnd = sameDay(date, end);
              const inRange = Boolean(start && end && date > start && date < end);
              return (
                <Day
                  type="button"
                  key={toISO(date)}
                  disabled={disabled}
                  $outside={outside}
                  $edge={isStart || isEnd}
                  $range={inRange}
                  onClick={() => chooseDate(date)}
                  aria-label={new Intl.DateTimeFormat('es-AR', { dateStyle: 'full' }).format(date)}
                >
                  {date.getDate()}
                </Day>
              );
            })}
          </Grid>
          <CalendarFoot><span>Seleccioná llegada y salida en este calendario</span>{start && <button type="button" onClick={() => { onChange({ checkIn: '', checkOut: '' }); setSelectingEnd(false); }}>Borrar fechas</button>}</CalendarFoot>
        </Calendar>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`position:relative;min-width:0;`;
const Trigger = styled.button`
  width:100%;min-height:66px;display:grid;grid-template-columns:auto 1fr auto 1fr;align-items:center;gap:13px;padding:10px 16px;
  color:#29474f;background:#fbfaf7;border:1px solid rgba(42,64,70,.14);border-radius:14px;font:inherit;text-align:left;cursor:pointer;
  transition:border-color .2s,box-shadow .2s;background .2s;
  >svg{color:#b45f40;font-size:20px;} &:hover,&:focus-visible{background:#fff;border-color:rgba(180,95,64,.55);box-shadow:0 0 0 4px rgba(180,95,64,.08);outline:none;}
  @media(max-width:520px){gap:9px;padding:9px 12px;}
`;
const DatePart = styled.span`min-width:0;small,strong{display:block;}small{margin-bottom:5px;color:#718083;font-size:12px;font-weight:720;letter-spacing:.08em;text-transform:uppercase;}strong{overflow:hidden;font-size:14px;font-weight:680;text-overflow:ellipsis;white-space:nowrap;&.empty{color:#9aa4a5;font-weight:520;}}`;
const Divider = styled.i`width:1px;height:34px;background:rgba(42,64,70,.12);`;
const Calendar = styled.div`
  position:absolute;top:calc(100% + 12px);left:0;z-index:40;width:min(390px,calc(100vw - 36px));padding:20px;background:#fff;
  border:1px solid rgba(42,64,70,.12);border-radius:20px;box-shadow:0 28px 70px rgba(27,47,52,.2);
  @media(max-width:520px){position:fixed;top:auto;right:12px;bottom:18px;left:12px;width:auto;}
`;
const CalendarTop = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;div small,div strong{display:block;}div small{margin-bottom:5px;color:#b45f40;font-size:12px;font-weight:760;letter-spacing:.14em;}div strong{color:#20373c;font-size:21px;font-weight:610;text-transform:capitalize;}`;
const Nav = styled.div`display:flex;gap:6px;button{display:grid;width:35px;height:35px;place-items:center;padding:0;color:#29474f;background:#f6f3ed;border:0;border-radius:50%;cursor:pointer;&:hover{color:#fff;background:#29474f;}}`;
const Week = styled.div`display:grid;grid-template-columns:repeat(7,1fr);margin-bottom:5px;color:#899496;font-size:12px;font-weight:720;text-align:center;`;
const Grid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);`;
const Day = styled.button`
  position:relative;min-width:0;height:42px;padding:0;color:${({$edge}) => $edge ? '#fff' : '#29474f'};background:${({$edge,$range}) => $edge ? '#b45f40' : $range ? '#f3ded4' : 'transparent'};
  border:0;border-radius:${({$edge,$range}) => $range ? '0' : $edge ? '50%' : '10px'};font:inherit;font-size:13px;font-weight:${({$edge}) => $edge ? '740' : '540'};cursor:pointer;
  opacity:${({$outside}) => $outside ? '.35' : '1'};&:hover:not(:disabled){color:${({$edge}) => $edge ? '#fff' : '#b45f40'};background:${({$edge}) => $edge ? '#a65335' : '#f8ebe5'};} &:disabled{opacity:.2;cursor:not-allowed;}
`;
const CalendarFoot = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:14px;padding-top:14px;border-top:1px solid rgba(42,64,70,.1);span{color:#7c898b;font-size:12px;}button{padding:0;color:#b45f40;background:none;border:0;font:inherit;font-size:12px;font-weight:720;cursor:pointer;white-space:nowrap;}`;
